import { call, put, takeEvery, takeLatest } from 'redux-saga/effects';
import { httpRequestsOnLoadActions } from '../http_requests_on_load';
import { httpRequestsOnSuccessActions } from '../http_requests_on_success';
import { ADD_TAG_TO_MEMBER, CREATE_TAG, DELETE_TAG, EDIT_TAG, GET_TAGS, GET_TAGS_SUCCESS } from './tag.types';
import { authService } from './tag.service';
import { httpRequestsOnErrorsActions } from '../http_requests_on_errors';
import { GET_ADMINS_SUCCESS, GET_ADMIN_BY_ID_SUCCESS } from '../admin/admin.types';
import { GET_CLIENTS_SUCCESS, GET_CLIENT_BY_ID_SUCCESS } from '../client/client.types';
import { GET_RESOURCE_SUCCESS, GET_RESOURCE_BY_ID_SUCCESS } from '../resource/resource.types';
import { GET_GOALS_SUCCESS, GET_GOAL_BY_ID_SUCCESS } from '../clinical/goals/goal.types';
import { GET_LIBRARY_GOALS_SUCCESS, GET_LIBRARY_GOAL_BY_ID_SUCCESS } from '../library/library.types';
import { fileService } from '../file/file.service';
import { GET_FILES_SUCCESS } from '../file/file.type';
import { GET_TASKS_SUCCESS } from '../tasks/tasks.type';
import { GET_INVOICE_PAYMENTS_SUCCESS } from '../posting/invoicePayment/invoicePayment.type';
import { GET_INVOICE_BY_ID_SUCCESS } from '../billing/invoice/invoice.type';
import { canReadTags } from './tag.utils';

/** Tags */

function* getTags({ payload, type }) {
    // Every list page dispatches this on mount, unconditionally. Without TAG_READ or TAG_MANAGE the
    // API answers 403, so short-circuit here rather than have a dozen call sites each repeat the
    // check — and rather than paint an error state for a catalog the user was never meant to see.
    if (!canReadTags()) {
        yield put({ type: GET_TAGS_SUCCESS, payload: [], module: payload?.params?.module });
        return;
    }

    yield put(httpRequestsOnErrorsActions.removeError(type));
    yield put(httpRequestsOnLoadActions.appendLoading(type));
    try {
        const res = yield call(authService.getTagsService, payload?.params);
        yield put({
            type: GET_TAGS_SUCCESS,
            payload: res.data,
            module: payload?.params?.module,
        });
        yield put(httpRequestsOnLoadActions.removeLoading(type));
        yield put(httpRequestsOnErrorsActions.removeError(type));
    } catch (err) {
        yield put(httpRequestsOnLoadActions.removeLoading(type));
        yield put(httpRequestsOnErrorsActions.appendError(type));
    }
}

function* createTag({ payload, type }) {
    yield put(httpRequestsOnErrorsActions.removeError(type));
    yield put(httpRequestsOnSuccessActions.removeSuccess(type));
    yield put(httpRequestsOnLoadActions.appendLoading(type));
    try {
        const created = yield call(authService.createTagService, payload?.body);

        if (created?.data?.id && payload?.assignBody && payload?.userInfo?.id) {
            // Take the caller's body verbatim and only prepend the new id. Rebuilding it here used to
            // drop `assignParams`, which the Document module needs (resourceModel/resourceId) — the
            // tag got created and then the assign died on "Unsupported document model".
            // Deduped because the API 400s when a tag id repeats: it compares found-count to sent-count.
            const tagIds = [...new Set([created.data.id, ...(payload.assignBody.tagIds || [])].filter(Boolean).map(String))];
            yield put({
                type: ADD_TAG_TO_MEMBER,
                payload: {
                    id: payload.userInfo.id,
                    body: { ...payload.assignBody, tagIds },
                    requestParams: payload?.requestParams,
                    module: payload?.body?.module,
                },
            });
        }

        const res = yield call(authService.getTagsService, payload?.params);
        yield put({
            type: GET_TAGS_SUCCESS,
            payload: res.data,
            module: payload?.params?.module,
        });
        yield put(httpRequestsOnSuccessActions.appendSuccess(type, created?.data));
        yield put(httpRequestsOnLoadActions.removeLoading(type));
        yield put(httpRequestsOnErrorsActions.removeError(type));
    } catch (err) {
        yield put(httpRequestsOnLoadActions.removeLoading(type));
        yield put(httpRequestsOnErrorsActions.appendError(type));
    }
}

function* editTag({ payload, type }) {
    yield put(httpRequestsOnErrorsActions.removeError(type));
    yield put(httpRequestsOnSuccessActions.removeSuccess(type));
    yield put(httpRequestsOnLoadActions.appendLoading(type));
    try {
        yield call(authService.editTagService, payload?.body);
        const res = yield call(authService.getTagsService, payload?.params);
        if (payload?.params?.module === 'Staff') {
            // Detail page passes { singleView, id } so we refresh the single staff; list pages refetch the list.
            if (payload?.requestParams?.singleView) {
                const res = yield call(authService.getAdminByIdService, payload?.requestParams?.id);
                yield put({
                    type: GET_ADMIN_BY_ID_SUCCESS,
                    payload: res.data,
                });
            } else {
                const res = yield call(authService.getAdminsService, payload?.requestParams);
                yield put({
                    type: GET_ADMINS_SUCCESS,
                    payload: { staff: res.data },
                });
            }
        }
        if (payload?.params?.module === 'Client') {
            // Detail page passes { singleView, id } so we refresh the single client; list pages refetch the list.
            if (payload?.requestParams?.singleView) {
                const res = yield call(authService.getClientByIdService, payload?.requestParams?.id);
                yield put({
                    type: GET_CLIENT_BY_ID_SUCCESS,
                    payload: res.data,
                });
            } else {
                const res = yield call(authService.getClientsService, payload?.requestParams);
                yield put({
                    type: GET_CLIENTS_SUCCESS,
                    payload: res.data,
                });
            }
        }
        if (payload?.params?.module === 'Resource') {
            if (payload?.requestParams?.singleView) {
                const res = yield call(authService.getResourceByIdService, payload?.requestParams?.id);
                yield put({
                    type: GET_RESOURCE_BY_ID_SUCCESS,
                    payload: res.data,
                });
            } else {
                const res = yield call(authService.getResourceService, payload?.requestParams);
                yield put({
                    type: GET_RESOURCE_SUCCESS,
                    payload: res.data,
                });
            }
        }
        if (payload?.params?.module === 'Goal') {
            // Detail page passes { singleView, id } so we refresh the single goal; list pages refetch the list.
            if (payload?.requestParams?.singleView) {
                const res = yield call(authService.getGoalByIdService, payload?.requestParams?.id);
                yield put({
                    type: GET_GOAL_BY_ID_SUCCESS,
                    payload: res.data,
                });
            } else {
                const res = yield call(authService.getGoalService, payload?.requestParams);
                yield put({
                    type: GET_GOALS_SUCCESS,
                    payload: res.data,
                });
            }
        }
        if (payload?.params?.module === 'LibraryGoal') {
            // Detail page passes { singleView, id } so we refresh the single library goal; list pages refetch the list.
            if (payload?.requestParams?.singleView) {
                const res = yield call(authService.getLibraryGoalByIdService, payload?.requestParams?.id);
                yield put({
                    type: GET_LIBRARY_GOAL_BY_ID_SUCCESS,
                    payload: res.data,
                });
            } else {
                const res = yield call(authService.getLibraryGoalsService, payload?.requestParams);
                yield put({
                    type: GET_LIBRARY_GOALS_SUCCESS,
                    payload: res.data,
                });
            }
        }
        if (payload?.params?.module === 'Document') {
            const res = yield call(fileService.getFilesService, payload?.requestParams);
            yield put({
                type: GET_FILES_SUCCESS,
                payload: res.data,
            });
        }
        if (payload?.params?.module === 'Task') {
            const res = yield call(authService.getTasksService, payload?.requestParams);
            yield put({
                type: GET_TASKS_SUCCESS,
                payload: res.data,
            });
        }
        if (payload?.params?.module === 'Invoice') {
            // Detail page passes { singleView, id } so we refresh the single invoice; list pages refetch the list.
            if (payload?.requestParams?.singleView) {
                const res = yield call(authService.getInvoiceByIdService, payload?.requestParams?.id);
                yield put({
                    type: GET_INVOICE_BY_ID_SUCCESS,
                    payload: { invoiceById: res.data },
                });
            } else {
                const res = yield call(authService.getInvoicesService, payload?.requestParams);
                yield put({
                    type: GET_INVOICE_PAYMENTS_SUCCESS,
                    payload: { invoicePayments: res.data },
                });
            }
        }
        yield put({
            type: GET_TAGS_SUCCESS,
            payload: res.data,
            module: payload?.params?.module,
        });
        yield put(httpRequestsOnErrorsActions.removeError(type));
        yield put(httpRequestsOnSuccessActions.appendSuccess(type));
        yield put(httpRequestsOnLoadActions.removeLoading(type));
    } catch (err) {
        yield put(httpRequestsOnLoadActions.removeLoading(type));
        yield put(httpRequestsOnErrorsActions.appendError(type));
    }
}

function* deleteTag({ payload, type }) {
    yield put(httpRequestsOnErrorsActions.removeError(type));
    yield put(httpRequestsOnSuccessActions.removeSuccess(type));
    yield put(httpRequestsOnLoadActions.appendLoading(type));
    try {
        yield call(authService.deleteTagService, payload?.id);

        const reqParams = {
            module: payload?.module,
        };

        const tags = yield call(authService.getTagsService, reqParams);

        if (payload?.module === 'Staff') {
            // Detail page passes { singleView, id } so we refresh the single staff; list pages refetch the list.
            if (payload?.requestParams?.singleView) {
                const res = yield call(authService.getAdminByIdService, payload?.requestParams?.id);
                yield put({
                    type: GET_ADMIN_BY_ID_SUCCESS,
                    payload: res.data,
                });
            } else {
                const res = yield call(authService.getAdminsService, payload?.requestParams);
                yield put({
                    type: GET_ADMINS_SUCCESS,
                    payload: { staff: res.data },
                });
            }
        }
        if (payload?.module === 'Client') {
            // Detail page passes { singleView, id } so we refresh the single client; list pages refetch the list.
            if (payload?.requestParams?.singleView) {
                const res = yield call(authService.getClientByIdService, payload?.requestParams?.id);
                yield put({
                    type: GET_CLIENT_BY_ID_SUCCESS,
                    payload: res.data,
                });
            } else {
                const res = yield call(authService.getClientsService, payload?.requestParams);
                yield put({
                    type: GET_CLIENTS_SUCCESS,
                    payload: res.data,
                });
            }
        }
        if (payload?.module === 'Resource') {
            // Detail page passes { singleView, id } so we refresh the single resource; list pages refetch the list.
            if (payload?.requestParams?.singleView) {
                const res = yield call(authService.getResourceByIdService, payload?.requestParams?.id);
                yield put({
                    type: GET_RESOURCE_BY_ID_SUCCESS,
                    payload: res.data,
                });
            } else {
                const res = yield call(authService.getResourceService, payload?.requestParams);
                yield put({
                    type: GET_RESOURCE_SUCCESS,
                    payload: res.data,
                });
            }
        }
        if (payload?.module === 'Goal') {
            // Detail page passes { singleView, id } so we refresh the single goal; list pages refetch the list.
            if (payload?.requestParams?.singleView) {
                const res = yield call(authService.getGoalByIdService, payload?.requestParams?.id);
                yield put({
                    type: GET_GOAL_BY_ID_SUCCESS,
                    payload: res.data,
                });
            } else {
                const res = yield call(authService.getGoalService, payload?.requestParams);
                yield put({
                    type: GET_GOALS_SUCCESS,
                    payload: res.data,
                });
            }
        }
        if (payload?.module === 'LibraryGoal') {
            // Detail page passes { singleView, id } so we refresh the single library goal; list pages refetch the list.
            if (payload?.requestParams?.singleView) {
                const res = yield call(authService.getLibraryGoalByIdService, payload?.requestParams?.id);
                yield put({
                    type: GET_LIBRARY_GOAL_BY_ID_SUCCESS,
                    payload: res.data,
                });
            } else {
                const res = yield call(authService.getLibraryGoalsService, payload?.requestParams);
                yield put({
                    type: GET_LIBRARY_GOALS_SUCCESS,
                    payload: res.data,
                });
            }
        }
        if (payload?.module === 'Document') {
            const res = yield call(fileService.getFilesService, payload?.requestParams);
            yield put({
                type: GET_FILES_SUCCESS,
                payload: res.data,
            });
        }
        if (payload?.module === 'Task') {
            const res = yield call(authService.getTasksService, payload?.requestParams);
            yield put({
                type: GET_TASKS_SUCCESS,
                payload: res.data,
            });
        }
        if (payload?.module === 'Invoice') {
            // Detail page passes { singleView, id } so we refresh the single invoice; list pages refetch the list.
            if (payload?.requestParams?.singleView) {
                const res = yield call(authService.getInvoiceByIdService, payload?.requestParams?.id);
                yield put({
                    type: GET_INVOICE_BY_ID_SUCCESS,
                    payload: { invoiceById: res.data },
                });
            } else {
                const res = yield call(authService.getInvoicesService, payload?.requestParams);
                yield put({
                    type: GET_INVOICE_PAYMENTS_SUCCESS,
                    payload: { invoicePayments: res.data },
                });
            }
        }

        yield put({
            type: GET_TAGS_SUCCESS,
            payload: tags.data,
            module: payload?.module,
        });
        yield put(httpRequestsOnSuccessActions.appendSuccess(type));
        yield put(httpRequestsOnLoadActions.removeLoading(type));
        yield put(httpRequestsOnErrorsActions.removeError(type));
    } catch (err) {
        yield put(httpRequestsOnLoadActions.removeLoading(type));
        yield put(httpRequestsOnErrorsActions.appendError(type));
    }
}

/** End */

/** Tag for members */
function* addTagToMember({ payload, type }) {
    yield put(httpRequestsOnErrorsActions.removeError(type));
    yield put(httpRequestsOnSuccessActions.removeSuccess(type));
    yield put(httpRequestsOnLoadActions.appendLoading(type));
    try {
        yield call(authService.addTagToMemberService, payload?.id, payload?.body);

        if (payload?.module === 'Staff') {
            // Detail page passes { singleView, id } so we refresh the single staff; list pages refetch the list.
            if (payload?.requestParams?.singleView) {
                const res = yield call(authService.getAdminByIdService, payload?.requestParams?.id);
                yield put({
                    type: GET_ADMIN_BY_ID_SUCCESS,
                    payload: res.data,
                });
            } else {
                const res = yield call(authService.getAdminsService, payload?.requestParams);
                yield put({
                    type: GET_ADMINS_SUCCESS,
                    payload: { staff: res.data },
                });
            }
        }
        if (payload?.module === 'Client') {
            // Detail page passes { singleView, id } so we refresh the single client; list pages refetch the list.
            if (payload?.requestParams?.singleView) {
                const res = yield call(authService.getClientByIdService, payload?.requestParams?.id);
                yield put({
                    type: GET_CLIENT_BY_ID_SUCCESS,
                    payload: res.data,
                });
            } else {
                const res = yield call(authService.getClientsService, payload?.requestParams);
                yield put({
                    type: GET_CLIENTS_SUCCESS,
                    payload: res.data,
                });
            }
        }
        if (payload?.module === 'Resource') {
            // Detail page passes { singleView, id } so we refresh the single resource; list pages refetch the list.
            if (payload?.requestParams?.singleView) {
                const res = yield call(authService.getResourceByIdService, payload?.requestParams?.id);
                yield put({
                    type: GET_RESOURCE_BY_ID_SUCCESS,
                    payload: res.data,
                });
            } else {
                const res = yield call(authService.getResourceService, payload?.requestParams);
                yield put({
                    type: GET_RESOURCE_SUCCESS,
                    payload: res.data,
                });
            }
        }
        if (payload?.module === 'Goal') {
            // Detail page passes { singleView, id } so we refresh the single goal; list pages refetch the list.
            if (payload?.requestParams?.singleView) {
                const res = yield call(authService.getGoalByIdService, payload?.requestParams?.id);
                yield put({
                    type: GET_GOAL_BY_ID_SUCCESS,
                    payload: res.data,
                });
            } else {
                const res = yield call(authService.getGoalService, payload?.requestParams);
                yield put({
                    type: GET_GOALS_SUCCESS,
                    payload: res.data,
                });
            }
        }
        if (payload?.module === 'LibraryGoal') {
            // Detail page passes { singleView, id } so we refresh the single library goal; list pages refetch the list.
            if (payload?.requestParams?.singleView) {
                const res = yield call(authService.getLibraryGoalByIdService, payload?.requestParams?.id);
                yield put({
                    type: GET_LIBRARY_GOAL_BY_ID_SUCCESS,
                    payload: res.data,
                });
            } else {
                const res = yield call(authService.getLibraryGoalsService, payload?.requestParams);
                yield put({
                    type: GET_LIBRARY_GOALS_SUCCESS,
                    payload: res.data,
                });
            }
        }
        if (payload?.module === 'Document') {
            const res = yield call(fileService.getFilesService, payload?.requestParams);
            yield put({
                type: GET_FILES_SUCCESS,
                payload: res.data,
            });
        }
        if (payload?.module === 'Task') {
            const res = yield call(authService.getTasksService, payload?.requestParams);
            yield put({
                type: GET_TASKS_SUCCESS,
                payload: res.data,
            });
        }
        if (payload?.module === 'Invoice') {
            // Detail page passes { singleView, id } so we refresh the single invoice; list pages refetch the list.
            if (payload?.requestParams?.singleView) {
                const res = yield call(authService.getInvoiceByIdService, payload?.requestParams?.id);
                yield put({
                    type: GET_INVOICE_BY_ID_SUCCESS,
                    payload: { invoiceById: res.data },
                });
            } else {
                const res = yield call(authService.getInvoicesService, payload?.requestParams);
                yield put({
                    type: GET_INVOICE_PAYMENTS_SUCCESS,
                    payload: { invoicePayments: res.data },
                });
            }
        }
        yield put(httpRequestsOnSuccessActions.appendSuccess(type));
        yield put(httpRequestsOnLoadActions.removeLoading(type));
        yield put(httpRequestsOnErrorsActions.removeError(type));
    } catch (err) {
        yield put(httpRequestsOnLoadActions.removeLoading(type));
        yield put(httpRequestsOnErrorsActions.appendError(type));
    }
}

/** End */

export const watchTags = function* watchTagsAuth() {
    /** Tags  */
    yield takeEvery(GET_TAGS, getTags);
    yield takeLatest(CREATE_TAG, createTag);
    yield takeLatest(EDIT_TAG, editTag);
    yield takeLatest(DELETE_TAG, deleteTag);
    /** End */

    /** Tag for members */
    yield takeLatest(ADD_TAG_TO_MEMBER, addTagToMember);
    /** End */
};
